import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip as MuiTooltip,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Theme } from '@mui/system';
import dinero from 'dinero.js';
import { parse as parseCsv } from 'json2csv';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import * as XLSX from 'xlsx';
import { OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint } from '../../../../../../hooks/useSextforceMetricsSubscribedAtVsTotalSalesDetails';
import { d2f } from '../../../../../../utils/common';

interface TransformedDataPoint {
    subscribedAt: string;
    count: number;
}

interface ChartDataPoint {
    time: number;
    count: number;
    total: number;
    displayTime?: string; // For display purposes
}

interface ExportData {
    headers: { label: string; value: string }[];
    rows: TransformedDataPoint[];
}

/**
 * Transforms the transactions data rows from the server to a format ready for export to CSV or Excel
 * @param rows Transactions rows
 * @param timezone Timezone string to convert the transaction time to
 * @returns Transformed data for export
 */
const transformTransactionsForExport = (
    rows: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] | undefined,
    timezone: string,
): ExportData => {
    if (!rows || rows.length === 0) {
        return { headers: [], rows: [] };
    }

    const headers = [
        {
            label: `Subscribed At ${timezone}`,
            value: 'subscribedAt',
        },
        {
            label: 'Fans',
            value: 'count',
        },
    ];

    // Rows without an interval, or whose interval day falls before the trial was created,
    // cannot be attributed to a date and are reported as "Unknown"
    const isUnknown = (row: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint): boolean =>
        !row._id.interval || moment(row._id.interval).tz(timezone).isBefore(moment(row._id.createdAt).tz(timezone), 'day');

    const filteredData = rows.filter(row => !isUnknown(row));

    const data: TransformedDataPoint[] = [];

    if (filteredData.length > 0) {
        // Iterate from the earliest date to the latest date and fill in missing dates with 0 counts.
        // All day arithmetic is done in the requested timezone - the server returns each interval
        // as midnight in that timezone, so comparing days in the browser's local timezone would
        // shift dates for users west of the selected timezone.
        const earliestDate = moment(filteredData[0]._id.interval).tz(timezone);
        const latestDate = moment().tz(timezone);

        for (let date = earliestDate.clone(); date.isBefore(latestDate); date.add(1, 'days')) {
            const foundData = filteredData.find(d => moment(d._id.interval).tz(timezone).isSame(date, 'day'));

            data.push({
                subscribedAt: date.format('L'),
                count: foundData ? foundData.count : 0,
            });
        }
    }

    // Add up all the data that could not be attributed to a date
    let unknownCount = 0;

    for (const row of rows) {
        if (isUnknown(row)) {
            unknownCount += row.count;
        }
    }

    if (unknownCount > 0) {
        data.push({
            subscribedAt: 'Unknown',
            count: unknownCount,
        });
    }

    return { headers, rows: data };
};

const generateData = (
    data: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] | undefined,
    showEarningsAsGross: boolean,
    showEarningsWithSubscriptions: boolean,
    viewByMonth: boolean,
    timezone: string,
) => {
    if (!data || data.length === 0) {
        return [];
    }

    const dateThreshold = moment(data[0]._id.createdAt).tz(timezone).startOf('day');

    // Filter data by date threshold
    const filteredData = data; //.slice().filter(d => dateThreshold.isSameOrBefore(d._id.interval));

    if (viewByMonth) {
        // Group by month when in month view
        const monthlyData: { [key: string]: ChartDataPoint } = {};

        filteredData.forEach(d => {
            let total: number = 0;

            if (showEarningsWithSubscriptions) {
                total = d.total ? (typeof d.total === 'number' ? d.total : d2f(d.total)) : 0;
            } else {
                total = d.totalWithoutSubscriptions
                    ? typeof d.totalWithoutSubscriptions === 'number'
                        ? d.totalWithoutSubscriptions
                        : d2f(d.totalWithoutSubscriptions)
                    : 0;
            }

            if (!showEarningsAsGross) {
                total = total * 0.8;
            }

            // Create month key in format YYYY-MM (in the requested timezone)
            const date = moment(d._id.interval).tz(timezone);
            const monthKey = date.format('YYYY-MM');
            const monthDisplay = date.format('MMM YYYY');

            // Initialize the month entry if it doesn't exist
            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {
                    time: date.clone().startOf('month').valueOf(),
                    displayTime: monthDisplay,
                    count: 0,
                    total: 0,
                };
            }

            // Aggregate the data
            monthlyData[monthKey].count += d.count;
            monthlyData[monthKey].total += total;
        });

        // Convert the grouped data object to an array and sort by time
        return {
            data: Object.values(monthlyData).sort((a, b) => a.time - b.time),
        };
    } else {
        const result = {
            data: filteredData
                .slice()
                .filter(d => dateThreshold.isSameOrBefore(d._id.interval))
                .map(d => {
                    let total: number = 0;

                    if (showEarningsWithSubscriptions) {
                        total = d.total ? (typeof d.total === 'number' ? d.total : d2f(d.total)) : 0;
                    } else {
                        total = d.totalWithoutSubscriptions
                            ? typeof d.totalWithoutSubscriptions === 'number'
                                ? d.totalWithoutSubscriptions
                                : d2f(d.totalWithoutSubscriptions)
                            : 0;
                    }

                    if (!showEarningsAsGross) {
                        total = total * 0.8;
                    }

                    return {
                        time: new Date(d._id.interval).valueOf(),
                        count: d.count,
                        total,
                    };
                }),
            // min: Math.round(Math.min(...data.map((d: any) => d.count))),
            // max: Math.round(Math.max(...data.map((d: any) => d.count))),
            // average: Math.round(data.reduce((a: any, b: any) => a + b.count, 0) / data.length),
        };

        return result;
    }
};

const CustomTooltip = ({ active, payload, showEarningsAsGross, timezone, theme }: any) => {
    if (active && payload && payload.length) {
        return (
            <div>
                <Grid
                    container
                    flexGrow={0}
                    alignItems="center"
                    spacing={1}
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: 4,
                        border: '1px solid #999',
                        maxWidth: 280,
                    }}
                >
                    <Grid item xs={12}>
                        <span style={{ fontWeight: 'bold' }}>{moment(payload[0].payload.time).tz(timezone).format('L')}</span>
                    </Grid>
                    {payload.map((item: any, index: number) => (
                        <Grid item xs={12} key={index}>
                            {item.dataKey === 'count' && (
                                <Grid container flexGrow={1} alignItems="center">
                                    <Grid item xs sx={{ color: theme.palette.primary.main }}>
                                        Claims:
                                    </Grid>
                                    <Grid
                                        item
                                        xs="auto"
                                        textAlign={'right'}
                                        sx={{ color: theme.palette.primary.main, paddingRight: '8px' }}
                                    >
                                        {item.value && typeof item.value === 'number' ? item.value.toLocaleString() : 0}
                                    </Grid>
                                </Grid>
                            )}

                            {item.dataKey === 'total' && (
                                <Grid container flexGrow={1} alignItems="center">
                                    <Grid item xs sx={{ color: theme.palette.secondary.main }}>
                                        Total Sales:
                                    </Grid>
                                    <Grid
                                        item
                                        xs="auto"
                                        textAlign={'right'}
                                        sx={{ color: theme.palette.primary.main, paddingRight: '8px', fontFamily: 'monospace' }}
                                    >
                                        {dinero({
                                            amount: item.value ? Math.trunc(item.value * 100) : 0,
                                            currency: 'USD',
                                        }).toFormat()}{' '}
                                        <small>({showEarningsAsGross ? 'gross' : 'net'})</small>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </div>
        );
    }

    return null;
};

type Props = {
    metricId: string;
    trialSubscribedAtVsSales: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] | undefined;
    trialSubscribedAtVsSalesLoading: boolean;
    showEarnings: boolean;
    showEarningsWithSubscriptions: boolean;
    showEarningsAsGross: boolean;
    timezone: string;
    theme: Theme;
};

const SextforceMetricsTrialSubscribedAtGraph = (props: Props) => {
    const {
        metricId,
        trialSubscribedAtVsSales,
        trialSubscribedAtVsSalesLoading,
        showEarnings,
        showEarningsAsGross,
        showEarningsWithSubscriptions,
        timezone,
        theme,
    } = props;
    const [dataMain, setDataMain] = useState<any>({ data: [], min: 0, max: 0, average: 0 });
    const [viewByMonth, setViewByMonth] = useState<boolean>(false);
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));

    useEffect(() => {
        if (!trialSubscribedAtVsSalesLoading && trialSubscribedAtVsSales && trialSubscribedAtVsSales.length > 0) {
            setDataMain(generateData(trialSubscribedAtVsSales, showEarningsAsGross, showEarningsWithSubscriptions, viewByMonth, timezone));
        } else {
            setDataMain([]);
        }
    }, [
        trialSubscribedAtVsSales,
        trialSubscribedAtVsSalesLoading,
        showEarningsAsGross,
        showEarningsWithSubscriptions,
        viewByMonth,
        timezone,
    ]);

    // Toggle between day and month view
    const handleViewToggle = (event: React.MouseEvent<HTMLElement>, newView: string) => {
        setViewByMonth(newView === 'month');
    };

    // Convert report to CSV format and start file download
    const handleDownloadReportCsv = () => {
        const transformedData: any = transformTransactionsForExport(trialSubscribedAtVsSales, timezone);
        // Format JSON data to CSV
        const csv = parseCsv(transformedData.rows, {
            fields: transformedData.headers,
            transforms: [],
        });
        // Convert CSV to Blob
        const blob: Blob = new Blob([csv], { type: 'text/csv' });
        // Create a descriptive filename
        const filename: string = `subscribersByDateCount_${metricId}_${moment().tz(timezone).format('YYYY-MM-DD')}.csv`;
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        // Append to html link element page
        document.body.appendChild(link);
        // Start download
        link.click();
        // Clean up and remove the link
        link.parentNode && link.parentNode.removeChild(link);
    };

    // Convert to Excel format and start file download
    const handleDownloadReportExcel = () => {
        const transformedData: any = transformTransactionsForExport(trialSubscribedAtVsSales, timezone);
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet([]);
        XLSX.utils.sheet_add_aoa(worksheet, [transformedData.headers.map((header: any) => header.label)]);
        XLSX.utils.sheet_add_json(worksheet, transformedData.rows, {
            origin: 'A2',
            skipHeader: true,
            header: transformedData.headers.map((header: any) => header.value),
        });
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
        // Create a descriptive filename
        const filename: string = `subscribersByDateCount_${metricId}_${moment().tz(timezone).format('YYYY-MM-DD')}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    const Chart = (chartData: any) => (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
                data={chartData && chartData.data ? chartData.data : []}
                margin={{
                    top: 0,
                    right: isLargeScreen ? 10 : 0,
                    left: isLargeScreen ? -20 : -20,
                    bottom: 60,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[400]} />
                <XAxis
                    dataKey="time"
                    name="Time"
                    tickFormatter={value => {
                        if (!value || value === 'auto') return '';

                        // If we're in month view, format as month year
                        if (viewByMonth) {
                            return moment(value).tz(timezone).format('MMM YYYY');
                        }

                        // Otherwise format as date
                        return moment(value).tz(timezone).format('L');
                    }}
                    domain={chartData && chartData.data && chartData.data.length > 0 ? ['dataMin', 'dataMax'] : ['auto', 'auto']}
                    interval={viewByMonth ? 0 : 'preserveStartEnd'} // Show all months when in month view
                    tick={{ fontSize: 13 }}
                    angle={viewByMonth ? -45 : -90}
                    dy={viewByMonth ? 20 : 35}
                    dx={viewByMonth ? 0 : -5}
                />
                <YAxis
                    yAxisId="count"
                    dataKey={'count'}
                    scale={'linear'}
                    name="Subscribers Count"
                    domain={[0, (dataMax: number) => Math.abs(dataMax * 1.1)]}
                    tickFormatter={(value: number) => value.toLocaleString()}
                    tick={{ fill: theme.palette.primary.main, fontSize: 13 }}
                />
                <Bar
                    yAxisId="count"
                    type="monotone"
                    dataKey="count"
                    barSize={viewByMonth ? 40 : 20}
                    maxBarSize={viewByMonth ? 40 : 20}
                    fill={theme.palette.primary.main}
                    isAnimationActive={false}
                />
                {showEarnings && (
                    <YAxis
                        yAxisId="total"
                        dataKey={'total'}
                        scale={'linear'}
                        name="Total Sales"
                        domain={['dataMin', (dataMax: number) => Math.abs(dataMax * 1.1)]}
                        tickFormatter={(value: number) =>
                            dinero({
                                amount: value && value !== Infinity ? Math.trunc(value * 100) : 0,
                                currency: 'USD',
                            }).toFormat()
                        }
                        orientation="right"
                        tick={{ fill: theme.palette.secondary.main, fontSize: 13 }}
                    />
                )}
                {showEarnings && (
                    <Line
                        yAxisId="total"
                        type="monotone"
                        dataKey="total"
                        stroke={theme.palette.secondary.main}
                        activeDot={{ r: 3 }}
                        dot={{ r: 2 }}
                        isAnimationActive={false}
                    />
                )}
                <Tooltip
                    content={
                        <CustomTooltip
                            payload={chartData.data}
                            showEarningsAsGross={showEarningsAsGross}
                            timezone={timezone}
                            theme={theme}
                        />
                    }
                />
                <Legend
                    verticalAlign="top"
                    height={36}
                    formatter={(value: string) => (
                        <span style={{ fontSize: 13 }}>{value === 'count' ? 'Claims Count' : 'Total Sales'}</span>
                    )}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );

    return (
        <Card sx={{ width: '100%', marginBottom: 2 }}>
            <CardContent>
                <Stack direction="column" spacing={1} sx={{ marginBottom: theme.spacing(1) }}>
                    <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography variant="h5" sx={{ marginBottom: theme.spacing(1) }}>
                            Claims Count vs. Sales by Date
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems={'center'}>
                            <MuiTooltip title="Download as CSV">
                                <span>
                                    <IconButton
                                        disabled={trialSubscribedAtVsSalesLoading}
                                        onClick={() => {
                                            handleDownloadReportCsv();
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faFileCsv} size="2x" color={theme.palette.info.main} />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                            <MuiTooltip title="Download as Excel">
                                <span>
                                    <IconButton
                                        disabled={trialSubscribedAtVsSalesLoading}
                                        onClick={() => {
                                            handleDownloadReportExcel();
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faFileExcel} size="2x" color={theme.palette.info.main} />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                        </Stack>
                    </Stack>
                    <Typography variant="body1" sx={{ marginBottom: theme.spacing(1) }}>
                        The following graph shows how many fans claimed the trial or promo link on each date, and how much money these fans
                        have spent since.
                    </Typography>
                    <Typography variant="body1">
                        Use this graph to identify when the spenders claimed the trial or promo vs. when the freeloaders came along.
                    </Typography>
                    <ToggleButtonGroup
                        value={viewByMonth ? 'month' : 'day'}
                        exclusive
                        onChange={handleViewToggle}
                        aria-label="View by"
                        size="small"
                        fullWidth={!isLargeScreen}
                    >
                        <ToggleButton value="day" aria-label="Day view">
                            <Typography variant="body2">Day View</Typography>
                        </ToggleButton>
                        <ToggleButton value="month" aria-label="Month view">
                            <Typography variant="body2">Month View</Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                <Grid container flexGrow={1} spacing={0} sx={{ padding: 0 }}>
                    <Grid item xs={12} sx={{ height: { xs: 300, sm: 500 } }}>
                        {trialSubscribedAtVsSalesLoading ? <Skeleton /> : Chart(dataMain)}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SextforceMetricsTrialSubscribedAtGraph;
